<template>
  <div class="mcp-consent d-flex justify-content-center align-items-start py-3 px-1">
    <b-card class="mcp-consent-card w-100">
      <!-- Loading -->
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
        <p class="text-muted mt-1 mb-0">
          Checking the request…
        </p>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="text-center py-2"
      >
        <div class="mcp-consent-icon bg-light-danger mx-auto mb-1">
          <feather-icon
            icon="AlertCircleIcon"
            size="26"
            class="text-danger"
          />
        </div>
        <h4 class="mb-50">
          Request could not be completed
        </h4>
        <p class="text-muted mb-0">
          {{ error }}
        </p>
      </div>

      <!-- Consent -->
      <template v-else-if="info">
        <div class="text-center mb-2">
          <div class="mcp-consent-icon bg-light-primary mx-auto mb-1">
            <feather-icon
              icon="ShieldIcon"
              size="26"
              class="text-primary"
            />
          </div>
          <h4 class="mb-50">
            Allow {{ info.client_name }} to access Curfox?
          </h4>
          <p class="text-muted mb-0">
            Signed in as <strong>{{ userLabel }}</strong>
          </p>
        </div>

        <div class="mb-1">
          <div class="text-muted small text-uppercase font-weight-bold mb-50">
            Claude will be able to
          </div>
          <ul class="list-unstyled mb-0">
            <li
              v-for="item in allowed"
              :key="item"
              class="d-flex align-items-start mb-50"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="16"
                class="text-success mr-50 mt-25"
              />
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>

        <div class="mb-2">
          <div class="text-muted small text-uppercase font-weight-bold mb-50">
            Claude will not be able to
          </div>
          <ul class="list-unstyled mb-0">
            <li
              v-for="item in notAllowed"
              :key="item"
              class="d-flex align-items-start mb-50"
            >
              <feather-icon
                icon="XCircleIcon"
                size="16"
                class="text-danger mr-50 mt-25"
              />
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>

        <p class="small text-muted mb-2">
          Every request is logged. You can disconnect at any time from Claude AI Reporting.
        </p>

        <b-button
          variant="primary"
          block
          :disabled="submitting"
          @click="submit(true)"
        >
          <b-spinner
            v-if="submitting && pendingApprove"
            small
            class="mr-50"
          />
          Allow access
        </b-button>
        <b-button
          variant="secondary"
          block
          class="mt-1"
          :disabled="submitting"
          @click="submit(false)"
        >
          Cancel
        </b-button>
      </template>
    </b-card>
  </div>
</template>

<script>
import mcpRepository from '@/repository/mcpRepository'
import {
  BButton,
  BCard,
  BSpinner,
} from 'bootstrap-vue'

const PARAMS = ['response_type', 'client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'state', 'scope']
export const PENDING_KEY = 'mcp_pending_authorize'

export default {
  components: {
    BButton,
    BCard,
    BSpinner,
  },
  data() {
    return {
      info: null,
      error: null,
      loading: true,
      submitting: false,
      pendingApprove: false,
      query: {},
      allowed: [
        'Read order reports for your company and the warehouses assigned to you',
        'See order status, COD, delivery charges, weights, delivery attempts',
        'See merchant, city and warehouse names',
      ],
      notAllowed: [
        'Change, create or delete any data',
        'See customer names, phone numbers, addresses or emails',
        'See data from warehouses you are not assigned to, or from other companies',
      ],
    }
  },
  computed: {
    userLabel() {
      try {
        const u = JSON.parse(localStorage.getItem('current_user'))
        return (u && (u.email || u.name)) || 'your account'
      } catch (e) {
        return 'your account'
      }
    },
  },
  async mounted() {
    // OAuth request parameters only (no secrets). Kept in sessionStorage so the login redirect can resume.
    let q = {}
    PARAMS.forEach(k => { if (this.$route.query[k]) q[k] = String(this.$route.query[k]) })
    if (!q.client_id) {
      try { q = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '{}') } catch (e) { q = {} }
    }
    this.query = q
    if (!this.$store.getters.isLoggedIn) {
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(q)) } catch (e) { /* ignore */ }
      this.$router.replace('/login')
      return
    }
    try { sessionStorage.removeItem(PENDING_KEY) } catch (e) { /* ignore */ }
    try {
      this.info = (await mcpRepository.getConsentInfo(q)).data.data
    } catch (e) {
      this.error = 'This request is invalid or has expired. Start again from Claude.'
    } finally {
      this.loading = false
    }
  },
  methods: {
    async submit(approve) {
      this.submitting = true
      this.pendingApprove = approve
      try {
        const { redirect_url: url } = (await mcpRepository.submitConsent({ ...this.query, approve })).data
        window.location.assign(url) // server-built, using the registered redirect URI
      } catch (e) {
        this.error = 'Could not complete the request. Start again from Claude.'
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.mcp-consent {
  min-height: 100vh;
}
.mcp-consent-card {
  max-width: 480px;
  margin: 0 auto;
}
.mcp-consent-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
